// The Coffre's thumb up/down control, extracted so Alignment can reuse the exact
// affordance the owner likes. Pure presentation — meaning comes from the parent:
//   • Coffre  → 👍 curates (mark reviewed/trusted), 👎 sends to Align.
//   • Align   → 👍 "the twin guessed right", 👎 "it guessed wrong" (a verdict).
// Always label them in context so the identical icon can't be confused between
// the curatorial thumb and the evaluative thumb.

import type { MouseEvent } from 'react';

type ThumbColor = 'emerald' | 'amber' | 'rose';

const IDLE: Record<ThumbColor, string> = {
  emerald: 'border-ink/15 text-ink/40 hover:text-emerald-300 hover:border-emerald-300/40',
  amber: 'border-ink/15 text-ink/40 hover:text-amber hover:border-amber/40',
  rose: 'border-ink/15 text-ink/40 hover:text-rose-300 hover:border-rose-300/40',
};
const ACTIVE: Record<ThumbColor, string> = {
  emerald: 'border-emerald-300/50 text-emerald-300 bg-emerald-300/[0.08]',
  amber: 'border-amber/50 text-amber bg-amber/[0.08]',
  rose: 'border-rose-300/50 text-rose-300 bg-rose-300/[0.08]',
};

export function ThumbButtons({
  onUp, onDown, upActive = false, downActive = false, busyUp = false, busyDown = false,
  upTitle, downTitle, downColor = 'amber', stop = true, className = '',
}: {
  onUp: () => void;
  onDown: () => void;
  upActive?: boolean;
  downActive?: boolean;
  busyUp?: boolean;
  busyDown?: boolean;
  upTitle?: string;
  downTitle?: string;
  downColor?: 'amber' | 'rose'; // Coffre = amber (send to Align); Align = rose (wrong)
  stop?: boolean;               // stopPropagation (cards that are themselves clickable)
  className?: string;
}) {
  const wrap = (fn: () => void) => (e: MouseEvent) => { if (stop) e.stopPropagation(); fn(); };
  const base = 'w-7 h-7 grid place-items-center rounded-full border text-[13px] transition-colors disabled:opacity-40';
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button onClick={wrap(onUp)} disabled={busyUp} title={upTitle}
        className={`${base} ${upActive ? ACTIVE.emerald : IDLE.emerald}`}>👍</button>
      <button onClick={wrap(onDown)} disabled={busyDown} title={downTitle}
        className={`${base} ${downActive ? ACTIVE[downColor] : IDLE[downColor]}`}>👎</button>
    </div>
  );
}
